import React, { useState, useRef } from 'react';
import { 
  Camera, 
  Download, 
  Upload, 
  X, 
  ZoomIn, 
  Loader2,
  AlertTriangle,
  Image as ImageIcon
} from 'lucide-react';

/**
 * IdentityPhotoViewer Component
 * 
 * Displays identity document photo with:
 * - Preview with zoom on click
 * - Full-screen modal view
 * - Download button
 * - Re-upload button (conditional)
 * - Loading state during image load
 * 
 * Requirements: 2.4
 */

interface IdentityPhotoViewerProps {
  photoUrl: string | null | undefined;
  canEdit: boolean;
  onReupload?: (file: File) => Promise<void>;
  title?: string;
  className?: string;
}

const IdentityPhotoViewer: React.FC<IdentityPhotoViewerProps> = ({
  photoUrl,
  canEdit,
  onReupload,
  title = 'Photo de la pièce d\'identité',
  className = '',
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageLoad = () => {
    setIsLoading(false);
    setHasError(false);
  };

  const handleImageError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const handleDownload = async () => {
    if (!photoUrl) return;

    try {
      // For base64 images
      if (photoUrl.startsWith('data:')) {
        const link = document.createElement('a');
        link.href = photoUrl;
        link.download = 'identity-document.jpg';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // For URL images
        const response = await fetch(photoUrl);
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = 'identity-document.jpg';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Erreur lors du téléchargement:', error);
      alert('Erreur lors du téléchargement de l\'image');
    }
  };

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !onReupload) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Veuillez sélectionner un fichier image (JPEG, PNG)');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('La taille du fichier ne doit pas dépasser 5 Mo');
      return;
    }

    try {
      setIsUploading(true);
      await onReupload(file);
    } catch (error) {
      console.error('Erreur lors de l\'upload:', error);
      alert('Erreur lors de l\'upload de la photo');
    } finally {
      setIsUploading(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  // No photo state
  if (!photoUrl) {
    return (
      <div className={`border-2 border-dashed border-gray-300 rounded-lg p-6 ${className}`}>
        <div className="text-center">
          <Camera className="w-12 h-12 text-gray-400 mx-auto mb-3" />
          <h4 className="text-sm font-medium text-gray-700 mb-1">{title}</h4>
          <p className="text-sm text-gray-500 mb-4">Aucune photo disponible</p>
          
          {canEdit && onReupload && (
            <>
              <button
                onClick={triggerFileSelect}
                disabled={isUploading}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Téléchargement...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Ajouter une photo
                  </>
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/jpg"
                onChange={handleFileSelect}
                className="hidden"
              />
            </>
          )}
        </div>
      </div>
    );
  }

  return (
    <>
      <div className={`${className}`}>
        <h4 className="text-sm font-medium text-gray-700 mb-2">{title}</h4>
        
        {/* Image container */}
        <div className="relative border-2 border-gray-200 rounded-lg overflow-hidden group">
          {/* Loading state */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center bg-gray-100">
              <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
            </div>
          )}

          {/* Error state */}
          {hasError && (
            <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100">
              <AlertTriangle className="w-8 h-8 text-red-500 mb-2" />
              <p className="text-sm text-gray-600">Erreur de chargement</p>
            </div>
          )}

          {/* Image */}
          <img
            src={photoUrl}
            alt={title}
            className={`w-full h-48 object-cover cursor-pointer transition-transform ${
              isLoading || hasError ? 'invisible' : 'visible'
            }`}
            onClick={() => !hasError && setIsModalOpen(true)}
            onLoad={handleImageLoad}
            onError={handleImageError}
          />

          {/* Hover overlay */}
          {!isLoading && !hasError && (
            <div 
              className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-30 transition-all flex items-center justify-center cursor-pointer"
              onClick={() => setIsModalOpen(true)}
            >
              <ZoomIn className="w-8 h-8 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
          )}
        </div>

        {/* Action buttons */}
        <div className="flex items-center gap-2 mt-3">
          <button
            onClick={handleDownload}
            disabled={hasError}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Download className="w-4 h-4" />
            Télécharger
          </button>

          {canEdit && onReupload && (
            <>
              <button
                onClick={triggerFileSelect}
                disabled={isUploading}
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isUploading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Envoi...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4" />
                    Remplacer
                  </>
                )}
              </button>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/jpeg,image/png,image/jpg"
                onChange={handleFileSelect}
                className="hidden"
              />
            </>
          )}
        </div>
      </div>

      {/* Full-screen modal */}
      {isModalOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-90 flex items-center justify-center z-50 p-4"
          onClick={() => setIsModalOpen(false)}
        >
          {/* Close button */}
          <button
            onClick={() => setIsModalOpen(false)}
            className="absolute top-4 right-4 p-2 bg-white rounded-full hover:bg-gray-100 transition-colors z-10"
          >
            <X className="w-6 h-6 text-gray-900" />
          </button>

          {/* Download button in modal */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleDownload();
            }}
            className="absolute top-4 left-4 p-2 bg-white rounded-full hover:bg-gray-100 transition-colors z-10"
            title="Télécharger"
          >
            <Download className="w-6 h-6 text-gray-900" />
          </button>

          {/* Image */}
          <img
            src={photoUrl}
            alt={title}
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export default IdentityPhotoViewer;
